import { AitLocation } from "aic";
import { AnnotationOptionType } from "components/annotations/interface";
import { useAppSettings } from "contexts/AppSettingsContext";
import { ClinicalData } from "contexts/tlf/getClinicalDataWithInheritance";
import { ClinicalVariableReadDto } from "functions/api/library/interface";
import { isEqual } from "lodash";
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from "react";
import { AnnotationWhereGroup } from "./AnnotationWhereGroup";
import { generatePreviewText } from "./generatePreviewText";
import { LogicalOperator } from "./LogicalOperator";
import { validateWhereClause } from "./validateWhereClause";
import { CompanionVariable, VariableDto } from "./VariableDto";
import { WhereClauseOp } from "./WhereClauseOp";

/**
 * State interface for annotation form controls.
 *
 * @interface AnnotationControlsState
 * @property selectedDataset - Selected clinical dataset.
 * @property selectedVariable - Selected clinical variable from the dataset (primary variable).
 * @property selectedTerminology - Name of the selected code list for mapped terminology.
 * @property value - User-entered or selected value(s) for comparison.
 * @property comparator - Name of the selected comparator operator.
 * @property additionalNotes - Free-text notes to accompany the annotation.
 * @property companionVariables - Array of companion variables with their types and values.
 */
export interface AnnotationControlsState {
  selectedDataset: string;
  selectedVariable?: ClinicalVariableReadDto;
  selectedTerminology?: string;
  value?: string;
  comparator?: string;
  additionalNotes?: string;
  companionVariables: CompanionVariable[];
}

/**
 * Props for the useAnnotationContextWindow hook.
 *
 * @interface UseAnnotationModalProps
 * @property annotationLocation - Table location where the annotation applies.
 * @property annotationType - Type of annotation (e.g., "Note", "Footnote").
 * @property clinicalLibrary - Clinical library data including datasets, code lists, etc.
 * @property initialAnnotation - Existing annotation data when editing.
 */
interface UseAnnotationModalProps {
  annotationLocation?: AitLocation;
  annotationType: AnnotationOptionType;
  clinicalLibrary?: ClinicalData;
  initialAnnotation?: AnnotationWhereGroup;
}

/**
 * Hook that encapsulates state and logic for the annotation modal form.
 *
 * @param props - Hook parameters
 * @param props.annotationLocation - Optional annotation location
 * @param props.annotationType - Annotation target type
 * @param props.clinicalLibrary - Optional clinical library data
 * @param props.initialAnnotation - Optional initial annotation to edit
 */
export const useAnnotationContextWindow = ({
  annotationLocation,
  annotationType,
  clinicalLibrary,
  initialAnnotation,
}: UseAnnotationModalProps) => {
  const appSettings = useAppSettings();
  const comparatorTypes = appSettings.annotationComparatorTypes;

  // #region Base Data
  const datasets = useMemo(() => clinicalLibrary?.clinicalDatasets ?? [], [clinicalLibrary]);
  const codeLists = useMemo(() => clinicalLibrary?.clinicalCodeLists ?? [], [clinicalLibrary]);
  // #endregion

  // #region Utility Functions

  /**
   * Find a dataset by name from the available datasets.
   *
   * @param name - Name of the dataset to search for
   * @returns Dataset if found, undefined otherwise
   */
  const findDatasetByName = useCallback(
    (name: string) => datasets.find((d) => d.name === name),
    [datasets],
  );

  /**
   * Creates baseline form state and clauses from an annotation.
   * Used for initializing and restoring annotation data.
   *
   * @param annotation - Annotation to extract state from
   * @returns Object containing form state and clauses
   */
  const createBaselineStateAndClauses = useCallback(
    (
      annotation: AnnotationWhereGroup,
    ): { formState: AnnotationControlsState; clauses: WhereClauseOp[] } => {
      const firstClause = annotation.clauses[0];
      if (!firstClause) {
        return {
          formState: {
            selectedDataset: "",
            selectedVariable: undefined,
            selectedTerminology: "",
            value: "",
            comparator: undefined,
            additionalNotes: annotation.additionalNotes ?? "",
            companionVariables: [],
          },
          clauses: [],
        };
      }

      const dataset = findDatasetByName(firstClause.left.dataset);
      const variable = dataset?.clinicalVariables.find(
        (v) => v.name === firstClause?.left.nameVariable,
      );
      const mappedCodeList = codeLists.find((cl) => cl.id === variable?.clinicalCodeListId);

      return {
        formState: {
          selectedDataset: dataset?.name ?? "",
          selectedVariable: variable,
          selectedTerminology: mappedCodeList?.name ?? "",
          value: firstClause?.right ?? "",
          comparator: firstClause?.comparator,
          additionalNotes: annotation.additionalNotes ?? "",
          companionVariables: firstClause.left.companionVariables ?? [],
        },
        clauses: annotation.clauses,
      };
    },
    [findDatasetByName, codeLists],
  );

  /**
   * Creates a VariableDto from a clinical variable and dataset name.
   *
   * @param variable - Clinical variable to convert
   * @param datasetName - Name of the Dataset containing the variable
   * @returns VariableDto with mapped properties
   */
  const createVariableDto = (
    variable: ClinicalVariableReadDto,
    datasetName: string,
  ): VariableDto => ({
    nameVariable: variable.name,
    dataset: datasetName,
  });

  /**
   * Creates an empty where clause with default values.
   *
   * @returns New WhereClauseOp with empty values and unique ID
   */
  const createEmptyClause = (): WhereClauseOp => ({
    id: crypto.randomUUID(),
    left: { nameVariable: "", dataset: "" },
    comparator: undefined,
    right: "",
  });
  // #endregion

  // #region State Management
  const [whereClauses, setWhereClauses] = useState<WhereClauseOp[]>(() => {
    if (initialAnnotation) {
      return createBaselineStateAndClauses(initialAnnotation).clauses;
    }
    return [];
  });

  const [currentClauseIndex, setCurrentClauseIndex] = useState(0);

  const [formState, setFormState] = useState<AnnotationControlsState>(() => {
    if (initialAnnotation) {
      return createBaselineStateAndClauses(initialAnnotation).formState;
    }
    return {
      selectedDataset: "",
      selectedVariable: undefined,
      selectedTerminology: "",
      value: "",
      comparator: undefined,
      additionalNotes: "",
      companionVariables: [],
    };
  });

  // Track previous values to detect changes
  const prevInitialAnnotationRef = useRef(initialAnnotation);
  const prevDatasetsLengthRef = useRef(datasets.length);

  /**
   * Effect event for updating form state from annotation.
   */
  const updateFormFromAnnotation = useEffectEvent(() => {
    if (initialAnnotation) {
      const { formState: newFormState, clauses } = createBaselineStateAndClauses(initialAnnotation);
      setFormState(newFormState);
      setWhereClauses(clauses);
      setCurrentClauseIndex(0);
    } else {
      setFormState({
        selectedDataset: "",
        selectedVariable: undefined,
        selectedTerminology: "",
        value: "",
        comparator: undefined,
        additionalNotes: "",
        companionVariables: [],
      });
      setWhereClauses([]);
      setCurrentClauseIndex(0);
    }
  });

  /**
   * Sync state when initialAnnotation changes or when clinical library loads.
   * This ensures the form displays the correct values when reopening with a saved annotation,
   * and also handles the case where the clinical library loads lazily after the component mounts.
   */
  useEffect(() => {
    const annotationChanged = !isEqual(prevInitialAnnotationRef.current, initialAnnotation);
    const clinicalLibraryLoaded =
      prevDatasetsLengthRef.current === 0 && datasets.length > 0 && initialAnnotation;

    // Update if annotation changed OR if clinical library just loaded while we have an annotation
    if (annotationChanged || clinicalLibraryLoaded) {
      prevInitialAnnotationRef.current = initialAnnotation;
      prevDatasetsLengthRef.current = datasets.length;
      updateFormFromAnnotation();
    }
  }, [initialAnnotation, datasets]);
  // #endregion

  // #region Additional Utility Functions

  /**
   * Creates a where clause from form state and variable data.
   *
   * @param variable - Clinical variable for the clause
   * @param datasetName - Name of the Dataset containing the variable
   * @param existingId - Optional ID to preserve when updating
   * @param precedingLogic - Optional logical operator for multi-clause groups
   * @returns New WhereClauseOp with form state data
   */
  const createWhereClause = (
    variable: ClinicalVariableReadDto,
    datasetName: string,
    existingId?: string,
    precedingLogic?: LogicalOperator,
  ): WhereClauseOp => {
    const baseDto = createVariableDto(variable, datasetName);
    const left: VariableDto = {
      ...baseDto,
      ...(formState.companionVariables.length > 0 && {
        companionVariables: formState.companionVariables,
      }),
    };
    return {
      id: existingId ?? crypto.randomUUID(),
      left,
      comparator: formState.comparator,
      right: formState.value,
      precedingLogic,
    };
  };

  /**
   * Helper to build the current annotation object from form and clause state.
   * Used for isUnchanged and buildFinalAnnotation.
   * Removes precedingLogic from the first clause, preserves for others.
   *
   * @returns Current annotation object or undefined if no valid clauses
   */
  const getCurrentAnnotationObject = (): AnnotationWhereGroup | undefined => {
    const allClauses = [...whereClauses];

    if (formState.selectedDataset && formState.selectedVariable) {
      const currentWhereClause = createWhereClause(
        formState.selectedVariable,
        formState.selectedDataset,
        whereClauses[currentClauseIndex]?.id,
        whereClauses[currentClauseIndex]?.precedingLogic,
      );
      allClauses[currentClauseIndex] = currentWhereClause;
    }

    const validClauses = allClauses.filter((clause) =>
      validateWhereClause(clause, comparatorTypes),
    );

    if (validClauses.length === 0) {
      return undefined;
    }

    const clausesWithCorrectLogic = validClauses.map((clause, index) => {
      if (index === 0) {
        const { precedingLogic: _precedingLogic, ...clauseWithoutLogic } = clause;
        return clauseWithoutLogic;
      } else {
        return clause;
      }
    });

    return {
      id: initialAnnotation?.id ?? crypto.randomUUID(),
      clauses: clausesWithCorrectLogic,
      additionalNotes: formState.additionalNotes,
    };
  };
  // #endregion

  // #region Derived Data
  const availableVariables = useMemo(
    () =>
      formState.selectedDataset
        ? (findDatasetByName(formState.selectedDataset)?.clinicalVariables ?? [])
        : [],
    [formState.selectedDataset, findDatasetByName],
  );

  const datasetOptions = useMemo(
    () =>
      datasets.map((dataset) => ({
        name: dataset.name,
      })),
    [datasets],
  );

  const variableOptions = useMemo(
    () =>
      availableVariables.map((variable) => ({
        displayValue: `${variable.name} - ${variable.dataType}`,
        name: variable.name,
      })),
    [availableVariables],
  );
  // #endregion

  // #region Form Management

  /**
   * Resets the form to empty state and clears all clauses.
   * Should be called when closing or canceling the modal.
   */
  const resetForm = () => {
    setFormState({
      selectedDataset: "",
      selectedVariable: undefined,
      selectedTerminology: "",
      value: "",
      comparator: undefined,
      additionalNotes: "",
      companionVariables: [],
    });
    setWhereClauses([]);
    setCurrentClauseIndex(0);
  };

  /**
   * Creates baseline form state and clauses from an annotation using current datasets and codeLists.
   * Used for initializing and restoring annotation data with up-to-date clinical library data.
   *
   * @param annotation - The annotation to extract state from
   * @returns Object containing form state and clauses
   */
  const getBaselineStateAndClauses = (annotation: AnnotationWhereGroup) => {
    const firstClause = annotation.clauses[0];
    if (!firstClause) {
      return {
        formState: {
          selectedDataset: "",
          selectedVariable: undefined,
          selectedTerminology: "",
          value: "",
          comparator: undefined,
          additionalNotes: annotation.additionalNotes ?? /* istanbul ignore next */ "",
          companionVariables: [],
        },
        clauses: [],
      };
    }

    const dataset = datasets.find((d) => d.name === firstClause.left.dataset);
    const variable = dataset?.clinicalVariables.find(
      (v) => v.name === firstClause?.left.nameVariable,
    );
    const mappedCodeList = codeLists.find((cl) => cl.id === variable?.clinicalCodeListId);

    return {
      formState: {
        selectedDataset: dataset?.name ?? /* istanbul ignore next */ "",
        selectedVariable: variable,
        selectedTerminology: mappedCodeList?.name ?? /* istanbul ignore next */ "",
        value: firstClause?.right ?? /* istanbul ignore next */ "",
        comparator: firstClause?.comparator,
        additionalNotes: annotation.additionalNotes ?? /* istanbul ignore next */ "",
        companionVariables: firstClause.left.companionVariables ?? [],
      },
      clauses: annotation.clauses,
    };
  };

  /**
   * Restores the form to the initial annotation state.
   * Should be called when canceling edits to revert to initial values.
   */
  const restoreInitial = () => {
    if (initialAnnotation) {
      const { formState: initialFormState, clauses } =
        getBaselineStateAndClauses(initialAnnotation);
      setFormState(initialFormState);
      setWhereClauses(clauses);
      setCurrentClauseIndex(0);
    } else {
      resetForm();
    }
  };
  // #endregion

  // #region Validation and State Derived Properties
  const isFormValid = (() => {
    // Check if current unsaved clause is valid (including companion variables check)
    const currentClauseIsValid = validateWhereClause(
      {
        selectedDataset: formState.selectedDataset,
        selectedVariable: formState.selectedVariable,
        comparator: formState.comparator,
        value: formState.value,
        companionVariables: formState.companionVariables,
      },
      comparatorTypes,
    );

    // Check all saved clauses (excluding the current one being edited)
    const savedClausesExcludingCurrent = whereClauses.filter(
      (_, idx) => idx !== currentClauseIndex,
    );
    const allOtherClausesValid = savedClausesExcludingCurrent.every((clause) =>
      validateWhereClause(clause, comparatorTypes),
    );

    // Allow saving when there's an initial annotation and all clauses have been removed (to enable removing all annotations)
    const canRemoveAllAnnotations =
      initialAnnotation && whereClauses.length === 0 && !currentClauseIsValid;

    // Form is valid if:
    // 1. All other saved clauses are valid AND current clause is valid, OR
    // 2. We're removing all (existing) annotations
    return !!((allOtherClausesValid && currentClauseIsValid) || canRemoveAllAnnotations);
  })();

  const isCurrentClauseValid = validateWhereClause(
    {
      selectedDataset: formState.selectedDataset,
      selectedVariable: formState.selectedVariable,
      comparator: formState.comparator,
      value: formState.value,
      companionVariables: formState.companionVariables,
    },
    comparatorTypes,
  );

  const canAddNewClause = isCurrentClauseValid;
  // #endregion

  // #region Clause Management
  /**
   * Saves the current form state as a where clause in the clauses list.
   * Updates existing clause at current index or creates new one.
   */
  const saveCurrentClause = () => {
    if (!formState.selectedDataset || !formState.selectedVariable) return;

    const whereClause = createWhereClause(
      formState.selectedVariable,
      formState.selectedDataset,
      whereClauses[currentClauseIndex]?.id,
      whereClauses[currentClauseIndex]?.precedingLogic,
    );

    setWhereClauses((prev) => {
      const updated = [...prev];
      updated[currentClauseIndex] = whereClause;
      return updated;
    });
  };

  /**
   * Loads a saved clause into the form for editing.
   *
   * @param clauseIndex - Index of the clause to load
   */
  const loadClauseIntoForm = useCallback(
    (clauseIndex: number) => {
      const clause = whereClauses[clauseIndex];
      const dataset = findDatasetByName(clause.left.dataset);
      const variable = dataset?.clinicalVariables.find((v) => v.name === clause.left.nameVariable);
      const mappedCodeList = codeLists.find((cl) => cl.id === variable?.clinicalCodeListId);

      setFormState((prev) => ({
        ...prev,
        selectedDataset: dataset?.name ?? /* istanbul ignore next */ "",
        selectedVariable: variable,
        selectedTerminology: mappedCodeList?.name ?? /* istanbul ignore next */ "",
        value: clause.right ?? /* istanbul ignore next */ "",
        comparator: clause.comparator,
        companionVariables: clause.left.companionVariables ?? [],
      }));
    },
    [codeLists, findDatasetByName, whereClauses],
  );

  /**
   * Navigate to the previous clause, saving the current clause if valid.
   */
  const goToPreviousClause = () => {
    /* istanbul ignore else */
    if (currentClauseIndex > 0) {
      if (isCurrentClauseValid) {
        saveCurrentClause();
      }
      setCurrentClauseIndex(currentClauseIndex - 1);
      loadClauseIntoForm(currentClauseIndex - 1);
    }
  };

  /**
   * Navigate to the next clause, saving the current clause if valid.
   */
  const goToNextClause = () => {
    /* istanbul ignore else */
    if (currentClauseIndex < whereClauses.length - 1) {
      if (isCurrentClauseValid) {
        saveCurrentClause();
      }
      setCurrentClauseIndex(currentClauseIndex + 1);
      loadClauseIntoForm(currentClauseIndex + 1);
    }
  };

  /**
   * Adds a new empty where clause and navigates to it.
   * Saves current clause before adding if it's valid.
   * New clause will have precedingLogic set to the specified logical operator.
   *
   * @param logicalOperator - Logical operator for the new clause (AND/OR)
   */
  const addNewWhereClause = (logicalOperator: LogicalOperator) => {
    const newClause = createEmptyClause();
    // Set precedingLogic from the button that was clicked
    newClause.precedingLogic = logicalOperator;

    setWhereClauses((prev) => {
      const updated = [...prev];

      if (isCurrentClauseValid && formState.selectedDataset && formState.selectedVariable) {
        const whereClause = createWhereClause(
          formState.selectedVariable,
          formState.selectedDataset,
          prev[currentClauseIndex]?.id,
          prev[currentClauseIndex]?.precedingLogic,
        );

        if (currentClauseIndex >= updated.length) {
          while (updated.length <= currentClauseIndex) {
            updated.push(createEmptyClause());
          }
        }
        updated[currentClauseIndex] = whereClause;
      }

      updated.push(newClause);
      return updated;
    });

    const newIndex = isCurrentClauseValid
      ? Math.max(whereClauses.length, currentClauseIndex + 1)
      : whereClauses.length;
    setCurrentClauseIndex(newIndex);

    setFormState((prev) => ({
      ...prev,
      selectedVariable: undefined,
      selectedTerminology: "",
      value: "",
      selectedDataset: "",
      comparator: undefined,
      companionVariables: [],
    }));
  };

  /**
   * Remove the current clause and update the active clause index and form state.
   */
  const removeCurrentClause = () => {
    setWhereClauses((prev) => {
      const updated = [...prev];
      updated.splice(currentClauseIndex, 1);
      // After removal, if no clauses remain, reset formState
      if (updated.length === 0) {
        setFormState((prevForm) => ({
          selectedDataset: "",
          selectedVariable: undefined,
          selectedTerminology: "",
          value: "",
          comparator: undefined,
          additionalNotes: prevForm.additionalNotes, // Preserve notes
          companionVariables: [],
        }));
        setCurrentClauseIndex(0);
      } else {
        // Otherwise, move to previous clause or first
        const newIndex = Math.min(currentClauseIndex, Math.max(0, updated.length - 1));
        setCurrentClauseIndex(newIndex);
        // Load the clause at the new index
        const clause = updated[newIndex];
        /* istanbul ignore else */
        if (clause) {
          const dataset = findDatasetByName(clause.left.dataset);
          const variable = dataset?.clinicalVariables.find(
            (v) => v.name === clause.left.nameVariable,
          );
          const mappedCodeList = codeLists.find((cl) => cl.id === variable?.clinicalCodeListId);
          setFormState((prevForm) => ({
            ...prevForm,
            selectedDataset: dataset?.name ?? /* istanbul ignore next */ "",
            selectedVariable: variable,
            selectedTerminology: mappedCodeList?.name,
            value: clause.right,
            comparator: clause.comparator,
            companionVariables: clause.left.companionVariables ?? [],
          }));
        }
      }
      return updated;
    });
  };

  /**
   * Determines if the current clause can be removed.
   *
   * @returns True if there are clauses to remove
   */
  const canRemoveClause = whereClauses.length > 0;

  /**
   * Calculates total clause count including unsaved current clause.
   *
   * @returns Total number of clauses for display
   */
  const totalClauseCount =
    currentClauseIndex < whereClauses.length
      ? Math.max(whereClauses.length, 1)
      : Math.max(whereClauses.length + 1, 1);

  /**
   * Determines if the current state matches the initial annotation.
   * Compares the built annotation from current state with the initial annotation using deep equality.
   *
   * @returns True if no changes have been made
   */
  const isUnchanged = (() => {
    const currentAnnotation = getCurrentAnnotationObject();

    if (initialAnnotation) {
      return isEqual(currentAnnotation, initialAnnotation);
    }
    return currentAnnotation === undefined;
  })();

  /**
   * Builds the final annotation object from current state.
   * Returns undefined when all clauses are removed to enable annotation removal.
   * Does not perform any table I/O operations.
   * Ensures first clause has no precedingLogic, subsequent clauses have "AND".
   *
   * @returns Final annotation or undefined if all clauses removed
   */
  const buildFinalAnnotation = (): AnnotationWhereGroup | undefined => {
    saveCurrentClause();
    return getCurrentAnnotationObject();
  };
  // #endregion

  // #region Simplified Field Handlers
  /**
   * Generic handler for form field changes with special case handling.
   * Clears dependent fields when dataset changes.
   *
   * @param field - Name of the field being changed
   * @param value - New value for the field
   */
  const handleFieldChange = (field: keyof AnnotationControlsState, value: string) => {
    if (field === "selectedDataset") {
      setFormState((prev) => ({
        ...prev,
        selectedDataset: value,
        selectedVariable: undefined,
        selectedTerminology: "",
        value: "",
      }));
    } else if (field === "selectedVariable") {
      const variable = availableVariables?.find((v) => v.name === value);
      const codeList = variable?.clinicalCodeListId
        ? codeLists.find((cl) => cl.id === variable.clinicalCodeListId)
        : undefined;

      setFormState((prev) => ({
        ...prev,
        selectedVariable: variable,
        selectedTerminology: codeList?.name ?? "",
        value: "",
      }));
    } else {
      setFormState((prev) => ({ ...prev, [field]: value }));
    }
  };

  /**
   * Sets both dataset and variable fields in a single atomic update.
   * Used when selecting from "dataset.variable" format to avoid state update race conditions.
   *
   * @param datasetName - Name of the clinical dataset to select
   * @param variableName - Name of the clinical variable within the dataset to select
   */
  const setDatasetAndVariable = (datasetName: string, variableName: string) => {
    const dataset = datasets.find((d) => d.name === datasetName);
    const variable = dataset?.clinicalVariables.find((v) => v.name === variableName);

    if (dataset && variable) {
      const codeList = variable.clinicalCodeListId
        ? codeLists.find((cl) => cl.id === variable.clinicalCodeListId)
        : undefined;

      setFormState((prev) => ({
        ...prev,
        selectedDataset: datasetName,
        selectedVariable: variable,
        selectedTerminology: codeList?.name ?? "",
        value: "",
      }));
    }
  };
  // #endregion

  // #region Preview and Output
  /**
   * Generates preview text for all where clauses.
   * Includes current unsaved clause if valid.
   *
   * @returns Preview of the annotation logic
   */
  const previewText = (() => {
    const allClauses = [...whereClauses];

    if (formState.selectedVariable) {
      const selectedVariableDto = formState.selectedVariable;
      const variableDto: VariableDto = {
        nameVariable: selectedVariableDto.name,
        dataset: formState.selectedDataset,
        ...(formState.companionVariables.length > 0 && {
          companionVariables: formState.companionVariables,
        }),
      };

      const currentWhereClause: WhereClauseOp = {
        id: whereClauses[currentClauseIndex]?.id ?? crypto.randomUUID(),
        left: variableDto,
        comparator: formState.comparator,
        right: formState.value,
        precedingLogic: whereClauses[currentClauseIndex]?.precedingLogic,
      };

      allClauses[currentClauseIndex] = currentWhereClause;
    }

    const validClauses = allClauses.filter((clause) =>
      validateWhereClause(clause, comparatorTypes),
    );

    if (validClauses.length === 0) {
      return "No annotation clauses configured";
    }

    return generatePreviewText(validClauses, comparatorTypes);
  })();
  // #endregion

  /**
   * Update a single form field by name.
   *
   * @param field - Name of the field to update
   * @param value - New value for the field (type matches the field's type in AnnotationControlsState)
   */
  const updateFormField = <K extends keyof AnnotationControlsState>(
    field: K,
    value: AnnotationControlsState[K],
  ) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  return {
    // Core state and validation
    formState,
    isFormValid,
    isCurrentClauseValid,
    canAddNewClause,
    canRemoveClause,
    isUnchanged,
    initialAnnotation,

    // Core actions (wrapper functions to allow external calls)
    updateFormField,
    resetForm,
    restoreInitial,
    buildFinalAnnotation,

    // Centralized annotations field change handler
    handleFieldChange,
    setDatasetAndVariable,

    // Multi-clause management
    whereClauses,
    currentClauseIndex,
    goToPreviousClause,
    goToNextClause,
    addNewWhereClause,
    removeCurrentClause,
    saveCurrentClause,

    // Data for selectors
    datasetOptions,
    variableOptions,
    codeLists,
    comparatorTypes,
    annotationLocation,
    annotationType,

    // Preview data
    previewText,
    totalClauseCount,
  };
};
